import { Button } from "@gigpro/ui/Button";
import { DialogFooter } from "@gigpro/ui/Dialog";
import type { JobsSubscriptionStatus } from "@sidegigllc/api/client";
import { Link } from "@tanstack/react-router";
import { day, JOBS_PLAN_PRICE, jobsPaymentLabel } from "domains/hire/jobsPlan";
import type React from "react";

type JobsTrialStepProps = {
  locationId: number;
  locationName: string;
  status: JobsSubscriptionStatus;
  /** Only admins and Qwick staff start the trial; anyone else is asked to get an admin. */
  isAdmin: boolean;
  trialEndsAt: Date;
  submitLabel: string;
  /** Making working interviews, unlocking applicants from the teaser, or starting from the plan card. */
  purpose?: "workingInterviews" | "unlock" | "settings";
  isStarting: boolean;
  /** Inline card/bank form for an admin with nothing on file; the trial starts once it saves. */
  paymentForm?: React.ReactNode;
  /** Holds the start button, e.g. while the inline form loads. */
  startDisabled?: boolean;
  /** The location bills its organization, whose payment method the trial needs. */
  billedToOrganization?: boolean;
  onStart: () => void;
  onBack: () => void;
};

export const JobsTrialStep = ({
  locationId,
  locationName,
  status,
  isAdmin,
  trialEndsAt,
  submitLabel,
  purpose = "workingInterviews",
  isStarting,
  paymentForm,
  startDisabled = false,
  billedToOrganization = false,
  onStart,
  onBack,
}: JobsTrialStepProps) => {
  const end = day(trialEndsAt);
  const payment = jobsPaymentLabel(status);
  const billingLink = (label: string, className?: string) => (
    <Link
      className={className}
      to="/locations/$locationId/billing"
      params={{ locationId: String(locationId) }}
      target="_blank"
      preload={false}
    >
      {label}
    </Link>
  );

  const title =
    purpose === "workingInterviews"
      ? "Start your 7-day free trial to make these working interviews"
      : "Start your 7-day free trial";

  if (!isAdmin) {
    return (
      <div className="space-y-4 pt-4">
        <h3 className="text-extraLarge font-semibold">{title}</h3>
        <p>Only an admin at {locationName} can start the free trial. Ask one of them to start it.</p>
        <DialogFooter className="mt-6">
          <Button variant="contained" onClick={onBack}>
            Back
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4">
      <div className="space-y-2">
        <h3 className="text-extraLarge font-semibold">{title}</h3>
        {purpose === "workingInterviews" && (
          <p>
            Make working interviews at {locationName} and see every applicant. Your job posts either way. Starting the
            trial turns these picks into working interviews.
          </p>
        )}
        {purpose === "unlock" && <p>See every applicant at {locationName}, message them and schedule interviews.</p>}
        {purpose === "settings" && <p>See every applicant at {locationName}, message them and hire.</p>}
        <p className="font-semibold">
          Free until {end}, then {JOBS_PLAN_PRICE}. Cancel anytime before {end} and you won't be charged.
        </p>
      </div>
      <div className="rounded-sm bg-background p-3 text-small">
        <PaymentLine
          status={status}
          payment={payment}
          end={end}
          paymentForm={paymentForm}
          billedToOrganization={billedToOrganization}
          billingLink={billingLink}
        />
      </div>
      <DialogFooter className="mt-6 grid grid-cols-2 gap-2">
        <Button variant="text" onClick={onBack} disabled={isStarting}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onStart}
          loading={isStarting}
          disabled={(status.needs_payment_method && !paymentForm) || startDisabled || isStarting}
        >
          {submitLabel}
        </Button>
      </DialogFooter>
    </div>
  );
};

type PaymentLineProps = {
  status: JobsSubscriptionStatus;
  payment: string | null;
  end: string;
  paymentForm?: React.ReactNode;
  billedToOrganization: boolean;
  billingLink: (label: string, className?: string) => React.ReactNode;
};

const PaymentLine = ({ status, payment, end, paymentForm, billedToOrganization, billingLink }: PaymentLineProps) => {
  if (status.collection === "INVOICE")
    return (
      <p>
        From {end}, {JOBS_PLAN_PRICE} goes on your monthly Qwick invoice.
      </p>
    );
  if (!status.needs_payment_method && payment) {
    return (
      <p>
        We'll charge {payment} on {end}. {billingLink("Change", "text-brand underline")}
      </p>
    );
  }
  if (paymentForm) {
    return (
      <div className="space-y-3">
        <p>Add a card or bank account to start your free trial.</p>
        {paymentForm}
      </div>
    );
  }
  if (billedToOrganization) {
    return (
      // No billing link: the location's Billing page saves to the location, which would not unblock the trial.
      <p>
        This location is billed to its organization. An organization admin needs to add the organization's payment
        method before the free trial can start.
      </p>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2">
      <p>Add a payment method to start your free trial.</p>
      <Button size="small" variant="raised" asChild>
        {billingLink("Add payment method")}
      </Button>
    </div>
  );
};
